import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Timesheet Calendar Component
 *
 * Displays a calendar view of hours worked per day within a pay period.
 * Supports navigation between pay periods with arrow buttons.
 */
import { byInstant, localDayKey, MAX_SESSION_HOURS, netHoursByDay, pairWorkSessions, toPunchKind, } from '@lalohr/utils';
import { format, parseISO, eachDayOfInterval, startOfWeek, endOfWeek, isWithinInterval, isToday, isFuture, } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Clock, } from 'lucide-react';
import { useMemo } from 'react';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/**
 * Calculate hours worked for each day based on punches
 *
 * `timeZone` is the tenant's IANA zone. Days used to be keyed by the VIEWER's
 * browser date, so a punch near local midnight moved between days depending
 * on where the person opening the calendar was sitting, and disagreed with
 * both backends. The calendar GRID itself is still built from local `Date`
 * objects — those are bare calendar labels for the period, with no instant
 * attached — but which day a punch belongs to is now the tenant's call.
 */
function calculateDailyHours(punches, periodStart, periodEnd, timeZone) {
    const dailyHours = new Map();
    // Initialize all days in the period
    const allDays = eachDayOfInterval({ start: periodStart, end: periodEnd });
    for (const day of allDays) {
        const dateKey = format(day, 'yyyy-MM-dd');
        dailyHours.set(dateKey, {
            date: day,
            totalHours: 0,
            regularHours: 0,
            overtimeHours: 0,
            isInPeriod: true,
            punchCount: 0,
            hasIncomplete: false,
        });
    }
    const events = [];
    for (const punch of punches) {
        // Excludes only superseded punches. This used to be an ACTIVE-only
        // allowlist, which silently dropped every COMPLETED punch.
        if (punch.status === 'CORRECTED')
            continue;
        const dayData = dailyHours.get(localDayKey(punch.timestamp, timeZone));
        if (dayData) {
            dayData.punchCount++;
        }
        const kind = toPunchKind(punch.action);
        if (kind) {
            events.push({ at: punch.timestamp, kind });
        }
    }
    // Pair across the WHOLE period, not per day, so a shift that crosses
    // midnight still pairs. Each completed session is then credited to the
    // tenant-local day it CLOCKED IN on.
    //
    // That start-day rule is a change: this screen used to credit the clock-out
    // day, which put an overnight shift on the day after the employee began it
    // and split its hours away from the 8h overtime cap that the timesheet and
    // payroll views apply to the starting day. Only shifts that actually cross
    // local midnight move, which is nearly none of them.
    //
    // byInstant because this screen has always ordered punches by parsed time;
    // the helper's default string ordering would mis-sort punches that differ
    // only in sub-second precision. Breaks are subtracted once, by
    // `netHoursByDay` — an earlier version added pre-break work at BREAK_START
    // and then the whole session again at CLOCK_OUT, double-counting any day
    // that had a break in it.
    const sessions = pairWorkSessions(events, {
        compare: byInstant,
        maxSessionHours: MAX_SESSION_HOURS,
    });
    for (const [dateKey, hours] of netHoursByDay(sessions, timeZone)) {
        const dayData = dailyHours.get(dateKey);
        if (!dayData)
            continue;
        dayData.totalHours = hours;
        // Simple OT calculation: >8 hours in a day is overtime
        dayData.regularHours = Math.min(hours, 8);
        dayData.overtimeHours = Math.max(0, hours - 8);
    }
    // Mark days with incomplete sessions. A trailing clock-in with no clock-out
    // after it means the employee is still on the clock; flag both the day that
    // session started and today, as this screen has always done.
    const inOut = events
        .filter(e => e.kind === 'clock_in' || e.kind === 'clock_out')
        .sort(byInstant);
    const trailing = inOut[inOut.length - 1];
    if (trailing?.kind === 'clock_in') {
        for (const key of [
            localDayKey(trailing.at, timeZone),
            localDayKey(new Date(), timeZone),
        ]) {
            const dayData = dailyHours.get(key);
            if (dayData) {
                dayData.hasIncomplete = true;
            }
        }
    }
    return dailyHours;
}
/**
 * Format hours for display
 */
function formatHours(hours) {
    if (hours === 0)
        return '-';
    return hours.toFixed(2);
}
export function TimesheetCalendar({ payPeriod, punches, onPreviousPeriod, onNextPeriod, hasPreviousPeriod, hasNextPeriod, isLoading = false, }) {
    const periodStart = parseISO(payPeriod.startDate);
    const periodEnd = parseISO(payPeriod.endDate);
    // Which day a punch belongs to is the tenant's, not the viewer's.
    const timeZone = useTenantTimeZone();
    // Calculate daily hours
    const dailyHours = useMemo(() => {
        return calculateDailyHours(punches, periodStart, periodEnd, timeZone);
    }, [punches, payPeriod.startDate, payPeriod.endDate, timeZone]);
    // Get calendar grid (may include days outside the period for alignment)
    const calendarDays = useMemo(() => {
        const calendarStart = startOfWeek(periodStart, { weekStartsOn: 0 });
        const calendarEnd = endOfWeek(periodEnd, { weekStartsOn: 0 });
        return eachDayOfInterval({ start: calendarStart, end: calendarEnd }).map(date => {
            const dateKey = format(date, 'yyyy-MM-dd');
            const periodData = dailyHours.get(dateKey);
            const isInPeriod = isWithinInterval(date, { start: periodStart, end: periodEnd });
            return {
                date,
                dateKey,
                isInPeriod,
                totalHours: periodData?.totalHours || 0,
                regularHours: periodData?.regularHours || 0,
                overtimeHours: periodData?.overtimeHours || 0,
                punchCount: periodData?.punchCount || 0,
                hasIncomplete: periodData?.hasIncomplete || false,
            };
        });
    }, [dailyHours, periodStart, periodEnd]);
    // Group days into weeks
    const weeks = useMemo(() => {
        const result = [];
        for (let i = 0; i < calendarDays.length; i += 7) {
            result.push(calendarDays.slice(i, i + 7));
        }
        return result;
    }, [calendarDays]);
    // Format period display
    const periodDisplay = useMemo(() => {
        return `${format(periodStart, 'MMM d')} - ${format(periodEnd, 'MMM d, yyyy')}`;
    }, [periodStart, periodEnd]);
    const periodTypeLabel = useMemo(() => {
        const typeMap = {
            weekly: 'Weekly',
            biweekly: 'Biweekly',
            semimonthly: 'Semi-monthly',
            monthly: 'Monthly',
        };
        return typeMap[payPeriod.payPeriodType] || payPeriod.payPeriodType;
    }, [payPeriod.payPeriodType]);
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs(Button, { variant: "outline", size: "sm", onClick: onPreviousPeriod, disabled: !hasPreviousPeriod || isLoading, className: "gap-1", children: [_jsx(ChevronLeft, { className: "h-4 w-4" }), "Previous"] }), _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "flex items-center justify-center gap-2", children: [_jsx(Calendar, { className: "h-5 w-5 text-brand-600" }), _jsx("h2", { className: "text-lg font-semibold", children: periodDisplay })] }), _jsxs("div", { className: "flex items-center justify-center gap-2 mt-1", children: [_jsx("span", { className: "text-sm text-gray-500", children: periodTypeLabel }), _jsx("span", { className: cn("text-xs px-2 py-0.5 rounded-full font-medium", payPeriod.status === 'current'
                                            ? "bg-brand-100 text-brand-700"
                                            : "bg-gray-100 text-gray-600"), children: payPeriod.status === 'current' ? 'Current' : 'Completed' })] })] }), _jsxs(Button, { variant: "outline", size: "sm", onClick: onNextPeriod, disabled: !hasNextPeriod || isLoading, className: "gap-1", children: ["Next", _jsx(ChevronRight, { className: "h-4 w-4" })] })] }), _jsxs("div", { className: "grid grid-cols-3 gap-4", children: [_jsxs("div", { className: "bg-brand-50 border border-brand-200 rounded-lg p-4 text-center", children: [_jsx("p", { className: "text-xs text-brand-600 font-medium uppercase tracking-wide", children: "Total Hours" }), _jsx("p", { className: "text-2xl font-bold text-brand-700 mt-1", children: payPeriod.totalHours.toFixed(2) })] }), _jsxs("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 text-center", children: [_jsx("p", { className: "text-xs text-green-600 font-medium uppercase tracking-wide", children: "Regular" }), _jsx("p", { className: "text-2xl font-bold text-green-700 mt-1", children: payPeriod.regularHours.toFixed(2) })] }), _jsxs("div", { className: "bg-orange-50 border border-orange-200 rounded-lg p-4 text-center", children: [_jsx("p", { className: "text-xs text-orange-600 font-medium uppercase tracking-wide", children: "Overtime" }), _jsx("p", { className: "text-2xl font-bold text-orange-700 mt-1", children: payPeriod.overtimeHours.toFixed(2) })] })] }), _jsxs("div", { className: "border border-gray-200 rounded-lg overflow-hidden", children: [_jsx("div", { className: "grid grid-cols-7 bg-gray-50 border-b border-gray-200", children: WEEKDAYS.map(day => (_jsx("div", { className: "py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider", children: day }, day))) }), _jsx("div", { className: "divide-y divide-gray-200", children: weeks.map((week, weekIndex) => (_jsx("div", { className: "grid grid-cols-7 divide-x divide-gray-200", children: week.map((day) => {
                                const isDayToday = isToday(day.date);
                                const isDayFuture = isFuture(day.date);
                                const hasHours = day.totalHours > 0;
                                const hasOvertime = day.overtimeHours > 0;
                                return (_jsxs("div", { className: cn("min-h-[80px] p-2 transition-colors", !day.isInPeriod && "bg-gray-50 opacity-50", day.isInPeriod && isDayToday && "bg-brand-50 ring-2 ring-inset ring-brand-400", day.isInPeriod && !isDayToday && hasHours && "bg-green-50/50", day.isInPeriod && !isDayToday && !hasHours && !isDayFuture && "bg-gray-50/50"), children: [_jsxs("div", { className: "flex items-center justify-between mb-1", children: [_jsx("span", { className: cn("text-sm font-medium", isDayToday && "text-brand-700", !day.isInPeriod && "text-gray-400", day.isInPeriod && !isDayToday && "text-gray-700"), children: format(day.date, 'd') }), day.hasIncomplete && day.isInPeriod && (_jsx("span", { title: "Active session", children: _jsx(Clock, { className: "h-3 w-3 text-amber-500" }) }))] }), day.isInPeriod && (_jsx("div", { className: "space-y-0.5", children: hasHours ? (_jsxs(_Fragment, { children: [_jsx("div", { className: cn("text-lg font-bold", hasOvertime ? "text-orange-600" : "text-green-700"), children: formatHours(day.totalHours) }), _jsx("div", { className: "text-xs text-gray-500", children: hasOvertime ? (_jsxs("span", { className: "text-orange-600", children: ["+", day.overtimeHours.toFixed(1), " OT"] })) : (_jsx("span", { children: "hrs" })) })] })) : (!isDayFuture && (_jsx("div", { className: "text-sm text-gray-400", children: "-" }))) }))] }, day.dateKey));
                            }) }, weekIndex))) })] }), _jsxs("div", { className: "flex items-center justify-center gap-6 text-xs text-gray-500", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("div", { className: "w-3 h-3 rounded bg-green-50 border border-green-200" }), _jsx("span", { children: "Hours logged" })] }), _jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("div", { className: "w-3 h-3 rounded bg-orange-100 border border-orange-300" }), _jsx("span", { children: "Overtime day" })] }), _jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(Clock, { className: "h-3 w-3 text-amber-500" }), _jsx("span", { children: "Active session" })] }), _jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("div", { className: "w-3 h-3 rounded bg-brand-100 ring-2 ring-brand-400" }), _jsx("span", { children: "Today" })] })] })] }));
}
